export type FocusModePlatform =
  | 'desktop'
  | 'ios_safari'
  | 'android_chrome'
  | 'ios_pwa'
  | 'android_pwa'
  | 'webview'
  | 'mobile_other';

export interface PlatformBlockRuleInput {
  isMobile: boolean;
  isIos: boolean;
  isAndroid: boolean;
  isPWA: boolean;
  userAgent?: string;
}

export interface PlatformBlockRules {
  platform: FocusModePlatform;
  isMobile: boolean;
  isIos: boolean;
  isAndroid: boolean;
  isPWA: boolean;
  isWebView: boolean;
  shouldHandleKeyboardShortcuts: boolean;
  shouldHandleClipboardEvents: boolean;
  shouldHandleSelectStart: boolean;
  shouldHandleContextMenu: boolean;
  shouldTrackVisibilityChange: boolean;
  shouldBlockInternalNavigation: boolean;
  shouldHandleDragEvents: boolean;
}

type PlatformRuleOverrides = Omit<
  PlatformBlockRules,
  'platform' | 'isMobile' | 'isIos' | 'isAndroid' | 'isPWA' | 'isWebView'
>;

function detectWebView(userAgent: string): boolean {
  const normalizedUserAgent = userAgent.toLowerCase();

  // WebView detection is heuristic-based and intentionally conservative.
  const isIosWebView =
    /(iphone|ipad|ipod)/i.test(userAgent) &&
    !/safari/i.test(userAgent) &&
    !/crios|fxios|edgios/i.test(userAgent);

  const isAndroidWebView =
    /android/i.test(userAgent) &&
    (/; wv\)/i.test(userAgent) ||
      (normalizedUserAgent.includes('version/') &&
        normalizedUserAgent.includes('chrome/') &&
        !normalizedUserAgent.includes('safari/')));

  return isIosWebView || isAndroidWebView;
}

function resolvePlatform({
  isMobile,
  isIos,
  isAndroid,
  isPWA,
  userAgent = '',
}: PlatformBlockRuleInput): FocusModePlatform {
  const isWebView = detectWebView(userAgent);

  if (!isMobile) return 'desktop';
  if (isWebView) return 'webview';
  if (isPWA && isIos) return 'ios_pwa';
  if (isPWA && isAndroid) return 'android_pwa';
  if (isIos) return 'ios_safari';
  if (isAndroid) return 'android_chrome';
  return 'mobile_other';
}

function createPlatformBlockRules(
  input: PlatformBlockRuleInput,
  platform: FocusModePlatform,
  overrides: PlatformRuleOverrides,
): PlatformBlockRules {
  return {
    platform,
    isMobile: input.isMobile,
    isIos: input.isIos,
    isAndroid: input.isAndroid,
    isPWA: input.isPWA,
    isWebView: platform === 'webview',
    ...overrides,
  };
}

export function getPlatformBlockRules(
  input: PlatformBlockRuleInput,
): PlatformBlockRules {
  const platform = resolvePlatform(input);

  const desktopRules: PlatformRuleOverrides = {
    shouldHandleKeyboardShortcuts: true,
    shouldHandleClipboardEvents: true,
    shouldHandleSelectStart: false,
    shouldHandleContextMenu: true,
    shouldTrackVisibilityChange: true,
    shouldBlockInternalNavigation: true,
    shouldHandleDragEvents: true,
  };

  // Mobile keeps navigation and visibility tracking, but disables keyboard
  // shortcuts because Ctrl/Cmd flows are not a reliable control surface there.
  const sharedMobileRules: PlatformRuleOverrides = {
    shouldHandleKeyboardShortcuts: false,
    shouldHandleClipboardEvents: true,
    shouldHandleSelectStart: true,
    shouldHandleContextMenu: true,
    shouldTrackVisibilityChange: true,
    shouldBlockInternalNavigation: true,
    shouldHandleDragEvents: true,
  };

  switch (platform) {
    case 'desktop':
      return createPlatformBlockRules(input, platform, desktopRules);

    case 'android_chrome':
      return createPlatformBlockRules(input, platform, {
        ...sharedMobileRules,
        shouldHandleContextMenu: true,
      });

    case 'android_pwa':
      return createPlatformBlockRules(input, platform, {
        ...sharedMobileRules,
        shouldHandleContextMenu: true,
      });

    case 'ios_safari':
    case 'ios_pwa':
      // iOS limits beforeunload and may vary on context-menu interception.
      // Keep context-menu enabled as best-effort handling on long-press flows.
      return createPlatformBlockRules(input, platform, sharedMobileRules);

    case 'webview':
      // WebView behavior is host-dependent, so we stay with monitor-first rules.
      return createPlatformBlockRules(input, platform, sharedMobileRules);

    case 'mobile_other':
    default:
      return createPlatformBlockRules(input, platform, {
        ...sharedMobileRules,
        shouldHandleContextMenu: true,
      });
  }
}
